import React, { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import AppEntityAvatar, {
  AvatarSize,
} from '../../../../components/designe/app-entity-avatar/AppEntityAvatar';
import { Paths } from '../../../../shared/paths';
import { useNavigate } from 'react-router-dom';
import { useGetLanguages } from '../../../../services/redux/api/translationsAPI';
import { useStyles } from './ExerciseItem.styles';
import theme from '../../../../styles/theme';
import { Box, Typography } from '@mui/material';
import { IExercise } from '../../../../types/interfaces/exercise';
import { IExerciseProperty } from '../../../../types/interfaces/exercise-property';
import { useGetExerciseLoadFormulas } from '../../../../services/redux/api/appConfigApi';
import { ExerciseScope } from '../../../../shared/constants';
import { isExerciseTranslationIncomplete } from '../../../../utils/exerciseTranslations';

function exercisePropertyLabel(p?: IExerciseProperty | null): string {
  if (!p) return '-';
  const label = [p.name, p.nameKey].filter(Boolean).join(' — ');
  return label || '-';
}

function exercisePropertyList(items?: IExerciseProperty[]): string {
  if (!items?.length) return '-';
  const parts = items
    .map((i) => exercisePropertyLabel(i))
    .filter((s) => s !== '-');
  return parts.length ? parts.join(', ') : '-';
}

interface IExerciseItem {
  exercise: IExercise;
  getActionButtons?: (exercise: IExercise) => ReactNode[] | ReactNode;
  showTranslationsError?: boolean;
  showPublicStatus?: boolean;
  onExerciseClick?: (exercise: IExercise) => void;
}

const ExerciseItem = (props: IExerciseItem) => {
  const {
    exercise,
    getActionButtons,
    showTranslationsError: showTranslationsErrorProps = false,
    showPublicStatus = false,
    onExerciseClick,
  } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { classes } = useStyles(theme);
  const { data: languages } = useGetLanguages();
  const { data: formulas } = useGetExerciseLoadFormulas();

  const exerciseFormula = useMemo(() => {
    if (!exercise?.exerciseLoadFormulaId || !formulas) return null;
    return formulas.find((f) => f.id === exercise.exerciseLoadFormulaId);
  }, [exercise?.exerciseLoadFormulaId, formulas]);

  const showTranslationsError = useMemo(() => {
    if (!exercise || !languages || !showTranslationsErrorProps) return false;

    return isExerciseTranslationIncomplete(exercise, languages);
  }, [showTranslationsErrorProps, languages, exercise]);
  const isPublicExercise = exercise?.scope === ExerciseScope.public;

  return (
    <div
      className={classes.exerciseItemWrapper}
      onClick={() =>
        onExerciseClick
          ? onExerciseClick(exercise)
          : navigate(Paths.moderatorDashboard.exercise(exercise.id))
      }>
      <div className={classes.exerciseItemInfoWrapper}>
        <AppEntityAvatar entity={exercise.avatar} size={AvatarSize.m} />
        <div className={classes.exerciseInfoWrapper}>
          <div className={classes.exerciseName}>{exercise.title}</div>
          <div>
            <span className={classes.exerciseItemPropertyLabel}>
              {t('exercises.category')}
            </span>
            <span className={classes.exerciseItemPropertyValue}>
              {exercisePropertyLabel(exercise.category)}
            </span>
          </div>
          <div>
            <span className={classes.exerciseItemPropertyLabel}>
              {t('exercises.mainMuscleGroup')}:
            </span>
            <span className={classes.exerciseItemPropertyValue}>
              {exercisePropertyLabel(exercise.mainMuscleGroup)}
            </span>
          </div>
          <div>
            <span className={classes.exerciseItemPropertyLabel}>
              {t('exercises.additionalMuscles')}:
            </span>
            <span className={classes.exerciseItemPropertyValue}>
              {exercisePropertyList(exercise.additionalMuscles)}
            </span>
          </div>
          <div>
            <span className={classes.exerciseItemPropertyLabel}>
              {t('exercises.equipment')}
            </span>
            <span className={classes.exerciseItemPropertyValue}>
              {exercisePropertyLabel(exercise.equipment)}
            </span>
          </div>
          <div>
            <span className={classes.exerciseItemPropertyLabel}>
              {t('exercises.additionalEquipments')}
            </span>
            <span className={classes.exerciseItemPropertyValue}>
              {exercisePropertyList(exercise.additionalEquipments)}
            </span>
          </div>
          <div>
            <span className={classes.exerciseItemPropertyLabel}>
              {t('exercises.developedSkills')}:
            </span>
            <span className={classes.exerciseItemPropertyValue}>
              {exercisePropertyList(exercise.developedSkills)}
            </span>
          </div>
          {exerciseFormula && (
            <div>
              <span className={classes.exerciseItemPropertyLabel}>
                {t('exercises.calculationLoadFormula')}
              </span>
              <span className={classes.exerciseItemPropertyValue}>
                {exerciseFormula.name}
              </span>
            </div>
          )}
          {showPublicStatus && (
            <div>
              <span className={classes.exerciseItemPropertyLabel}>
                {t('exercises.scope')}:
              </span>
              <span
                className={`${classes.exerciseItemPropertyValue} ${
                  isPublicExercise
                    ? classes.publishedStatusSuccess
                    : classes.publishedStatusReject
                }`}>
                {isPublicExercise
                  ? t('exercises.scope_public')
                  : t('exercises.scope_private')}
              </span>
            </div>
          )}
          {showTranslationsError && (
            <Box className={classes.translationsError}>
              <Typography variant="text-md">
                {t('exercises.translationError')}
              </Typography>
            </Box>
          )}
        </div>
        <Box
          className={classes.buttonsContainer}
          onClick={(e) => e.stopPropagation()}>
          {getActionButtons && getActionButtons(exercise)}
        </Box>
      </div>
    </div>
  );
};

export default ExerciseItem;
